import { Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

import classes from "./Choice.module.css";
import { Soon } from "./Soon";

/** An option: its value and words, maybe live's icon, maybe Coming soon (live has it, we not yet). */
export interface ChoiceOption {
  value: string;
  label: string;
  icon?: ReactNode;
  soon?: boolean;
  /** Shown but not choosable now, as live's selects grey one (Dubbing's target equal to its source). */
  disabled?: boolean;
}

export interface ChoiceProps {
  setting: string;
  icon: ReactNode;
  value: string;
  options: ChoiceOption[];
  onChange: (value: string) => void;
  /** The trigger's width, as in the live app: 190 for the model, 100 the ratio, 80 the count. */
  width?: number;
  /** A chevron after the value: live shows one on the image ratio and count only. */
  chevron?: boolean;
  /** Greyed and closed, with live's reason as its tooltip ("Duration is set by the shots"). */
  disabledBecause?: string | undefined;
  /** Greyed and closed with no reason shown, as live's while its form is busy. */
  disabled?: boolean;
  /** Live's tooltip on the setting, shown on hover (the Sounds composer's). */
  tip?: string | undefined;
  /**
   * `footer`: a template form's setting, live's `footer-select` (unstyled, `hideChevron`): no
   * chevron and no grey on hover. `composer` (the default): the Studio's and the Sounds page's.
   */
  variant?: "composer" | "footer";
}

/**
 * A setting as the live composers' borderless select (the Studio's and the Sounds page's): icon,
 * chosen value, maybe a chevron.
 */
export function Choice(props: ChoiceProps) {
  const { setting, icon, value, options, onChange, width, disabledBecause, tip } = props;
  const footer = props.variant === "footer";
  const chevron = !footer && props.chevron !== false;
  const className = footer ? `${classes.choice} ${classes.footer}` : classes.choice;
  const chosen = options.find((each) => each.value === value) ?? options[0];
  if (disabledBecause !== undefined || props.disabled === true) {
    return (
      <Tooltip label={disabledBecause} disabled={disabledBecause === undefined} withArrow>
        {/* A disabled button takes no hover, so the tooltip hangs from a wrapper. */}
        <span className={classes.choiceOff}>
          <UnstyledButton
            className={className}
            style={width === undefined ? undefined : { width }}
            aria-label={`${setting}: ${chosen?.label}`}
            disabled
          >
            {icon}
            <span className={classes.choiceLabel}>{chosen?.label ?? ""}</span>
          </UnstyledButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <Menu position="top-start" withinPortal={false}>
      {/* Mantine's way: the tooltip around the menu's target. */}
      <Tooltip label={tip} disabled={tip === undefined} multiline maw={300} withArrow>
        <Menu.Target>
          <UnstyledButton
            className={className}
            style={width === undefined ? undefined : { width }}
            aria-label={`${setting}: ${chosen?.label}`}
          >
            {icon}
            <span className={classes.choiceLabel}>{chosen?.label ?? ""}</span>
            {chevron ? <ChevronDown size={14} className={classes.chevron} aria-hidden /> : null}
          </UnstyledButton>
        </Menu.Target>
      </Tooltip>
      {/* Mantine names the menu by its target, "Model: Gemini Image". Live's selects show about
          six rows and scroll the rest (Mantine's 220px, issue 347), the chosen one in view. */}
      <Menu.Dropdown className={classes.dropdown}>
        {options.map((option) =>
          option.soon === true ? (
            <Soon key={option.value}>
              {(soon) => (
                // Open after a click, so its tooltip still says why nothing happened.
                <Menu.Item {...soon} closeMenuOnClick={false} leftSection={option.icon}>
                  {option.label}
                </Menu.Item>
              )}
            </Soon>
          ) : (
            <Menu.Item
              key={option.value}
              leftSection={option.icon}
              disabled={option.disabled === true}
              ref={
                option.value === value
                  ? (item: HTMLElement | null) => item?.scrollIntoView?.({ block: "nearest" })
                  : undefined
              }
              onClick={() => onChange(option.value)}
              rightSection={option.value === value ? <Check size={16} /> : null}
            >
              {option.label}
            </Menu.Item>
          ),
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
